<template>
  <div class="main-content">
    <SearchBar ref="searchBarRef" />
    <div
      class="tile-grid"
      :class="{
        'with-calendar': dashboardStore.isCalendarVisible,
        'with-notes-tasks': hasNotesTasks,
        'single-window': !dashboardStore.isCalendarVisible && !hasNotesTasks,
      }"
    >
      <aside
        v-if="dashboardStore.isCalendarVisible"
        class="tile-window calendar-window"
      >
        <CalendarPanel />
      </aside>
      <section
        class="tile-window shortcuts-window"
        :class="{
          'span-rows':
            !hasNotesTasks ||
            (!dashboardStore.isCalendarVisible && hasNotesTasks),
        }"
      >
        <div class="shortcuts-center">
          <ShortcutGrid />
        </div>
        <div class="shortcuts-actions">
          <button
            type="button"
            class="shortcut-edit-btn"
            :aria-pressed="dashboardStore.isEditMode"
            @click="dashboardStore.toggleEditMode()"
          >
            <i
              :class="
                dashboardStore.isEditMode ? 'fas fa-check' : 'fas fa-edit'
              "
            ></i>
            {{ dashboardStore.isEditMode ? "完了" : "編集" }}
          </button>
        </div>
      </section>
      <section v-if="hasNotesTasks" class="tile-window notes-tasks-window">
        <div class="secondary-panels">
          <NotesPanel v-if="dashboardStore.isNotesVisible" />
          <TasksPanel v-if="dashboardStore.isTasksVisible" />
        </div>
      </section>
    </div>
    <QuickInputModal
      v-if="isQuickInputOpen"
      :mode="quickInputMode"
      @close="closeQuickInput"
      @submitted="handleQuickSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import SearchBar from "./SearchBar.vue";
import ShortcutGrid from "./ShortcutGrid.vue";
import NotesPanel from "./NotesPanel.vue";
import TasksPanel from "./TasksPanel.vue";
import CalendarPanel from "./CalendarPanel.vue";
import QuickInputModal from "./QuickInputModal.vue";
import { useDashboardStore } from "@/stores/dashboard";

type SearchBarExpose = {
  focusSearchInput: (clear?: boolean) => void;
  openFromShortcut: (clear?: boolean) => void;
  closePopover: () => void;
  isOpen: { value: boolean };
};

const searchBarRef = ref<SearchBarExpose | null>(null);
const dashboardStore = useDashboardStore();
const hasNotesTasks = computed(
  () =>
    dashboardStore.isNotesVisible ||
    dashboardStore.isTasksVisible ||
    dashboardStore.isSchedulesVisible,
);
const isQuickInputOpen = ref(false);
const quickInputMode = ref<"note" | "task" | "schedule">("note");

let shortcutSequenceArmed = false;
let shortcutSequenceTimerId: number | null = null;
let pendingShortcutDigits = "";
let slashSequenceCount = 0;
let slashSequenceTimerId: number | null = null;
let shouldClearSearchOnTripleSlash = false;

const getSearchInput = (): HTMLInputElement | null => {
  return document.querySelector(".search-input");
};

const isSearchPopoverOpen = (): boolean => {
  return Boolean(searchBarRef.value?.isOpen?.value);
};

const isTypingContext = (target: EventTarget | null): boolean => {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return (
    target.isContentEditable ||
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT"
  );
};

const openShortcutByNumber = (shortcutNumber: number): void => {
  const target = document.querySelector(
    `.shortcut-card[data-shortcut-number="${shortcutNumber}"]`,
  ) as HTMLElement | null;
  if (target) {
    target.click();
  }
};

const armShortcutSequence = (): void => {
  shortcutSequenceArmed = true;
  pendingShortcutDigits = "";
  if (shortcutSequenceTimerId) {
    window.clearTimeout(shortcutSequenceTimerId);
  }
};

const disarmShortcutSequence = (): void => {
  shortcutSequenceArmed = false;
  pendingShortcutDigits = "";
  if (shortcutSequenceTimerId) {
    window.clearTimeout(shortcutSequenceTimerId);
    shortcutSequenceTimerId = null;
  }
};

const scheduleSlashSequenceReset = (): void => {
  if (slashSequenceTimerId) {
    window.clearTimeout(slashSequenceTimerId);
  }
  slashSequenceTimerId = window.setTimeout(() => {
    slashSequenceCount = 0;
  }, 1000);
};

const focusSearchInput = (): void => {
  disarmShortcutSequence();
  slashSequenceCount = 0;

  searchBarRef.value?.openFromShortcut(shouldClearSearchOnTripleSlash);
  shouldClearSearchOnTripleSlash = false;
};

const openQuickInput = (mode: "note" | "task" | "schedule"): void => {
  disarmShortcutSequence();
  slashSequenceCount = 0;
  quickInputMode.value = mode;
  isQuickInputOpen.value = true;
};

const closeQuickInput = (): void => {
  isQuickInputOpen.value = false;
};

const handleQuickSubmit = async (
  mode: "note" | "task" | "schedule",
): Promise<void> => {
  if (mode === "note") {
    await dashboardStore.fetchNotes();
  } else if (mode === "task") {
    await dashboardStore.fetchTasks();
  } else {
    await dashboardStore.fetchSchedules();
  }
};

const scheduleShortcutResolve = (force = false): void => {
  if (shortcutSequenceTimerId) {
    window.clearTimeout(shortcutSequenceTimerId);
  }

  shortcutSequenceTimerId = window.setTimeout(
    () => {
      resolveShortcutSequence(force);
    },
    force ? 0 : 700,
  );
};

const getAvailableShortcutNumbers = (): number[] => {
  return Array.from(
    document.querySelectorAll(".shortcut-card[data-shortcut-number]"),
  )
    .map((el) => Number((el as HTMLElement).dataset.shortcutNumber))
    .filter((n) => Number.isInteger(n) && n > 0);
};

const resolveShortcutSequence = (force: boolean): void => {
  if (!shortcutSequenceArmed || !pendingShortcutDigits) return;

  const availableNumbers = getAvailableShortcutNumbers();
  const exactNumber = Number(pendingShortcutDigits);
  const hasExactMatch = availableNumbers.includes(exactNumber);
  const hasLongerPrefixMatch = availableNumbers.some(
    (n) =>
      String(n).startsWith(pendingShortcutDigits) &&
      String(n).length > pendingShortcutDigits.length,
  );

  if (
    hasExactMatch &&
    (force || !hasLongerPrefixMatch || pendingShortcutDigits.length >= 2)
  ) {
    openShortcutByNumber(exactNumber);
    disarmShortcutSequence();
    return;
  }

  if (force || pendingShortcutDigits.length >= 2 || !hasLongerPrefixMatch) {
    disarmShortcutSequence();
  }
};

const extractShortcutDigit = (event: KeyboardEvent): number | null => {
  if (/^[1-9]$/.test(event.key)) {
    return Number(event.key);
  }

  if (event.code.startsWith("Numpad")) {
    const digit = event.code.replace("Numpad", "");
    if (/^[1-9]$/.test(digit)) {
      return Number(digit);
    }
  }

  return null;
};

const handleShortcutSequence = (event: KeyboardEvent): boolean => {
  if (isSearchPopoverOpen()) {
    return false;
  }

  const input = getSearchInput();
  if (input && document.activeElement === input) {
    return false;
  }

  if (!shortcutSequenceArmed) {
    if (event.key === "/" && !isTypingContext(event.target)) {
      event.preventDefault();
      slashSequenceCount = 1;
      scheduleSlashSequenceReset();
      armShortcutSequence();
      scheduleShortcutResolve(true);
      return true;
    }

    return false;
  }

  const shortcutDigit = extractShortcutDigit(event);
  if (shortcutDigit !== null) {
    event.preventDefault();
    slashSequenceCount = 0;
    pendingShortcutDigits += String(shortcutDigit);

    if (pendingShortcutDigits.length >= 2) {
      resolveShortcutSequence(true);
    } else {
      scheduleShortcutResolve(false);
    }

    return true;
  }

  if (
    slashSequenceCount > 0 &&
    !pendingShortcutDigits &&
    !isTypingContext(event.target)
  ) {
    const key = event.key.toLowerCase();
    if (key === "m" || key === "t" || key === "s") {
      event.preventDefault();
      disarmShortcutSequence();
      slashSequenceCount = 0;
      if (key === "m") {
        openQuickInput("note");
      } else if (key === "s") {
        openQuickInput("schedule");
      } else {
        openQuickInput("task");
      }
      return true;
    }
  }

  if (
    event.key === "/" &&
    !isTypingContext(event.target) &&
    !pendingShortcutDigits
  ) {
    event.preventDefault();
    slashSequenceCount += 1;
    scheduleSlashSequenceReset();

    if (slashSequenceCount >= 3) {
      disarmShortcutSequence();
      slashSequenceCount = 0;
      focusSearchInput();
      return true;
    }

    return true;
  }

  if (event.key === "Enter") {
    event.preventDefault();
    resolveShortcutSequence(true);
    return true;
  }

  if (event.key !== "Shift") {
    slashSequenceCount = 0;
    disarmShortcutSequence();
  }

  return false;
};

const handleGlobalKeydown = (event: KeyboardEvent): void => {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
    event.preventDefault();
    if (!isQuickInputOpen.value) {
      openQuickInput("task");
    }
    return;
  }

  if (
    event.shiftKey &&
    event.code === "Space" &&
    !isTypingContext(event.target)
  ) {
    event.preventDefault();
    if (!isQuickInputOpen.value) {
      openQuickInput("note");
    }
    return;
  }

  if (handleShortcutSequence(event)) {
    return;
  }

  if (event.key === "Escape") {
    if (isQuickInputOpen.value) {
      closeQuickInput();
      event.preventDefault();
      return;
    }
    if (isSearchPopoverOpen()) {
      shouldClearSearchOnTripleSlash = true;
      searchBarRef.value?.closePopover();
      event.preventDefault();
    }

    disarmShortcutSequence();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleGlobalKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleGlobalKeydown);
});
</script>

<style scoped>
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding: 16px 16px;
  min-height: 0;
}

.tile-grid {
  width: 100%;
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  min-height: 0;
}

.tile-grid.single-window {
  grid-template-rows: minmax(0, 1fr);
}

/* ショートカット + メモ/タスク（カレンダーなし）：左右分割、右側上下 */
.tile-grid.with-notes-tasks:not(.with-calendar) {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
}

/* すべて表示（カレンダー＋ショートカット＋メモ/タスク）：左1/3、右上下 */
.tile-grid.with-calendar.with-notes-tasks {
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
}

/* カレンダー＋ショートカット（メモ/タスクなし）：左右分割 */
.tile-grid.with-calendar:not(.with-notes-tasks) {
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
  grid-template-rows: minmax(0, 1fr);
}

.tile-window {
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.calendar-window {
  grid-column: 1;
  grid-row: 1 / -1;
}

.shortcuts-window {
  grid-column: 1;
  grid-row: 1;
}

.shortcuts-window.span-rows {
  grid-row: 1 / -1;
}

.notes-tasks-window {
  grid-column: 2;
  grid-row: 2;
}

/* ショートカット + メモ/タスク のとき、メモ/タスクは右側全高 */
.tile-grid.with-notes-tasks:not(.with-calendar) .notes-tasks-window {
  grid-row: 1 / -1;
}

/* ショートカット + メモ/タスク のとき、ショートカットは左側全高 */
.tile-grid.with-notes-tasks:not(.with-calendar) .shortcuts-window {
  grid-row: 1 / -1;
}

/* 3ウィンドウ時は右上にショートカット、右下にメモ/タスク */
.tile-grid.with-calendar.with-notes-tasks .shortcuts-window {
  grid-column: 2;
  grid-row: 1;
}

.tile-grid.with-calendar.with-notes-tasks .notes-tasks-window {
  grid-column: 2;
  grid-row: 2;
}

.shortcuts-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
}

.shortcuts-actions {
  display: flex;
  justify-content: center;
  padding-bottom: 6px;
}

.shortcut-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: white;
  color: #5f6368;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.shortcut-edit-btn:hover {
  background: #f5f5f5;
  border-color: #d0d0d0;
}

.shortcut-edit-btn[aria-pressed="true"] {
  background: #1f73e6;
  color: white;
  border-color: #1f73e6;
}

.notes-tasks-window {
  overflow: auto;
}

.secondary-panels {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  min-height: 0;
}

@media (max-width: 1100px) {
  .tile-grid.with-calendar.with-notes-tasks,
  .tile-grid.with-calendar:not(.with-notes-tasks) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  }

  .tile-window {
    padding: 12px;
    border-radius: 12px;
  }
}

@media (max-width: 900px) {
  .main-content {
    padding: 12px;
  }

  .tile-grid {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
  }

  .tile-grid.with-calendar.with-notes-tasks {
    grid-template-rows: auto auto auto;
  }

  .tile-grid.with-notes-tasks:not(.with-calendar) {
    grid-template-rows: auto auto;
  }

  .calendar-window,
  .shortcuts-window,
  .notes-tasks-window {
    grid-column: 1;
    grid-row: auto;
  }

  .tile-grid.with-calendar.with-notes-tasks .calendar-window {
    grid-row: 1;
  }

  .tile-grid.with-calendar.with-notes-tasks .shortcuts-window {
    grid-row: 2;
  }

  .tile-grid.with-calendar.with-notes-tasks .notes-tasks-window {
    grid-row: 3;
  }

  .tile-grid.with-notes-tasks:not(.with-calendar) .shortcuts-window {
    grid-row: 1;
  }

  .tile-grid.with-notes-tasks:not(.with-calendar) .notes-tasks-window {
    grid-row: 2;
  }

  .shortcuts-window.span-rows {
    grid-row: auto;
  }
}

@media (max-width: 640px) {
  .tile-grid {
    gap: 8px;
  }

  .secondary-panels {
    grid-template-columns: 1fr;
  }
}
</style>
